import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Col, Row, Form, Modal, Divider, Checkbox } from 'antd';
import { LocalizationContext } from '../../../hoc/Localization';
import InputNumber from '../../Input/InputNumber';
import TextArea from '../../Input/TextArea';
import RangePicker from '../../Input/RangePicker';
import Input from '../../Input';
import Select from '../../Select';

import { EditorContext } from '../../../hoc/Editor';
import { SettingsContext } from '../../../hoc/Settings';

import styles from './styles.less';

const PriceModal = ({ priceModal, setPriceModal }) => {
  const { t } = React.useContext(LocalizationContext);
  const { edited, updateContent } = React.useContext(EditorContext);
  const {
    domainSettings: { defaultCurrency },
  } = React.useContext(SettingsContext);

  const [freeCourse, setFreeCourse] = React.useState(edited.free_course);
  const [custom, setCustom] = React.useState(edited.period === 'custom');
  const [fieldValues, setFieldValues] = React.useState({});
  const [touched, setTouched] = React.useState(false);
  const [form] = Form.useForm();

  const { validateFields, setFieldsValue } = form;

  const FormItem = Form.Item;
  const dateFormat = 'YYYY-MM-DD HH:mm';

  const formItemSettings = {
    labelCol: {
      span: 10,
    },
    wrapperCol: {
      span: 14,
    },
  };

  const periods = [
    {
      title: t('course.details.content.month'),
      value: 'month',
    },
    {
      title: t('course.details.content.course'),
      value: 'course',
    },
    {
      title: t('course.details.content.hour'),
      value: 'hour',
    },
    {
      title: t('course.details.content.per_custom'),
      value: 'custom',
    },
  ];

  const currencies = [
    {
      title: 'AMD',
      value: 'AMD',
    },
    {
      title: 'EUR',
      value: 'EUR',
    },
    {
      title: 'RUB',
      value: 'RUB',
    },
  ];

  React.useEffect(() => {
    const {
      price,
      period,
      currency,
      discount,
      free_trial: freeTrial,
      period_value: periodValue,
      discount_type: discountType,
      discount_start: discountStart,
      discount_end: discountEnd,
      early_bird_end: earlyBirdEnd,
      last_minute_end: lastMinuteEnd,
      early_bird_start: earlyBirdStart,
      last_minute_start: lastMinuteStart,
      early_bird_message: earlyBirdMessage,
      early_bird_discount: earlyBirdDiscount,
      last_minute_message: lastMinuteMessage,
      last_minute_discount: lastMinuteDiscount,
    } = edited;

    const values = {
      price,
      period,
      currency: currency || defaultCurrency,
      discount,
      free_trial: freeTrial,
      discount_type: discountType,
      early_bird_message: earlyBirdMessage,
      last_minute_message: lastMinuteMessage,
      early_bird_discount: earlyBirdDiscount,
      last_minute_discount: lastMinuteDiscount,
      discount_start: discountStart,
      discount_end: discountEnd,
      early_bird_dates: [
        earlyBirdStart ? moment(earlyBirdStart, dateFormat) : null,
        earlyBirdEnd ? moment(earlyBirdEnd, dateFormat) : null,
      ].filter(x => x !== null),
      last_minute_dates: [
        lastMinuteStart ? moment(lastMinuteStart, dateFormat) : null,
        lastMinuteEnd ? moment(lastMinuteEnd, dateFormat) : null,
      ].filter(x => x !== null),
      discount_dates: [
        discountStart ? moment(discountStart, dateFormat) : null,
        discountEnd ? moment(discountEnd, dateFormat) : null,
      ].filter(x => x !== null),
    };
    if (custom) {
      values.period_value = periodValue;
    }

    setFieldsValue(values);
  }, [edited]);

  const collectPriceValues = () => {
    validateFields().then(values => {
      const data = values;

      if (data.early_bird_dates) {
        data.early_bird_start = data.early_bird_dates[0] && data.early_bird_dates[0].toISOString();
        data.early_bird_end = data.early_bird_dates[1] && data.early_bird_dates[1].toISOString();
      }

      if (data.last_minute_dates) {
        data.last_minute_start =
          data.last_minute_dates[0] && data.last_minute_dates[0].toISOString();
        data.last_minute_end = data.last_minute_dates[1] && data.last_minute_dates[1].toISOString();
      }

      if (data.discount_dates) {
        data.discount_start = data.discount_dates[0] && data.discount_dates[0].toISOString();
        data.discount_end = data.discount_dates[1] && data.discount_dates[1].toISOString();
      }

      delete data.early_bird_dates;
      delete data.last_minute_dates;
      delete data.discount_dates;

      if (!data.early_bird_discount) {
        data.early_bird_start = null;
        data.early_bird_end = null;
      }

      if (!data.last_minute_discount) {
        data.last_minute_start = null;
        data.last_minute_end = null;
      }

      if (!data.discount) {
        data.discount_start = null;
        data.discount_end = null;
      }

      if (freeCourse) {
        data.price = 0;
      }

      if (data.discount_type === 'amount') {
        data.discounted_price = data.discount;
        data.discount_percent = null;
      } else if (data.discount_type === 'percentage') {
        data.discounted_price = (data.price * data.discount) / 100;
        data.discount_percent = data.discount;
      } else {
        data.discounted_price = null;
        data.discount_percent = null;
      }

      const content = {
        ...edited,
        ...data,
        free_course: freeCourse,
      };

      updateContent(content);
      setPriceModal(false);
    });
  };

  const getValues = () => {
    setTouched(true);
    validateFields()
      .then(values => {
        setFieldValues(values);
      })
      .catch(e => setFieldValues(e.values));
  };

  const handleFreeCourse = free => {
    setFreeCourse(free);
    setFieldsValue({ price: '' });
  };

  let disc = fieldValues.discount;
  let discDates = fieldValues.discount_dates;
  let early = fieldValues.early_bird_discount;
  let earlyDates = fieldValues.early_bird_dates;
  let earlyMessage = fieldValues.early_bird_message;
  let lastMinute = fieldValues.last_minute_discount;
  let lastMinuteDates = fieldValues.last_minute_dates;
  let lastMinuteMessage = fieldValues.last_minute_message;

  if (!touched) {
    disc = edited.discount;
    discDates = edited.discount_dates;
    early = edited.early_bird_discount;
    earlyDates = edited.early_bird_dates;
    earlyMessage = edited.early_bird_message;
    lastMinute = edited.last_minute_discount;
    lastMinuteDates = edited.last_minute_dates;
    lastMinuteMessage = edited.last_minute_message;
  }

  const { price, currency } = edited;

  const discountFilled = disc || (discDates && discDates[0]);
  const earlyFilled = early || (earlyDates && earlyDates[0]) || earlyMessage;
  const lastFilled = lastMinute || (lastMinuteDates && lastMinuteDates[0]) || lastMinuteMessage;

  const discountDisabled = !!((earlyFilled || lastFilled) && !discountFilled);
  const earlybirdDisabled = !!((discountFilled || lastFilled) && !earlyFilled);
  const lastminuteDisabled = !!((discountFilled || earlyFilled) && !lastFilled);

  return (
    <Modal
      title={t('details.edit.courses.form.priceamount')}
      visible={priceModal}
      onOk={collectPriceValues}
      onCancel={() => {
        setPriceModal(false);
      }}
      className={styles.modal}
    >
      <Form form={form} onValuesChange={() => getValues()}>
        <FormItem label={t('details.edit.courses.form.priceamount')} {...formItemSettings}>
          <>
            <Row gutter={10}>
              <Col xs={8}>
                <FormItem name={['price']} rules={[{ required: !freeCourse }]}>
                  <InputNumber
                    style={{ width: '100%' }}
                    disabled={freeCourse}
                    initialValues={price}
                  />
                </FormItem>
              </Col>
              <Col xs={8}>
                <FormItem name={['currency']}>
                  <Select
                    version="short"
                    options={currencies}
                    renderOption={option => option.title}
                    disabled={freeCourse}
                    initialValues={currency || defaultCurrency}
                  />
                </FormItem>
              </Col>
              <Col xs={8}>
                <FormItem name={['period']}>
                  <Select
                    version="short"
                    options={periods}
                    renderOption={option => option.title}
                    disabled={freeCourse}
                    onChange={value => {
                      if (custom && value !== 'custom') {
                        setCustom(false);
                        setFieldsValue({ period_value: '' });
                      } else if (value === 'custom') {
                        setCustom(true);
                      }
                    }}
                  />
                </FormItem>
              </Col>
            </Row>
            <Row>
              <Col xs={10}>
                <FormItem>
                  <Checkbox
                    checked={freeCourse}
                    onChange={value => {
                      handleFreeCourse(value.target.checked);
                    }}
                  >
                    {t('details.course.free')}
                  </Checkbox>
                </FormItem>
              </Col>
              <Col xs={14}>
                <FormItem name={['free_trial']}>
                  <Checkbox>{t('details.edit.courses.form.free_trial')}</Checkbox>
                </FormItem>
              </Col>

              {custom && (
                <FormItem name={['period_value']}>
                  <Input
                    maxLength={40}
                    version="long"
                    placeholder={t('max.length.40.characters')}
                  />
                </FormItem>
              )}
            </Row>
          </>
        </FormItem>
        <Divider />
        <FormItem label={t('course.details.edit.discount')} {...formItemSettings}>
          <Row gutter={10}>
            <Col xs={12}>
              <FormItem name={['discount']}>
                <InputNumber disabled={discountDisabled} />
              </FormItem>
            </Col>
            <Col xs={12}>
              <FormItem name={['discount_type']}>
                <Select
                  version="long"
                  disabled={discountDisabled}
                  options={[
                    { id: 'amount', value: 'Amount' },
                    { id: 'percentage', value: 'Percentage' },
                  ]}
                />
              </FormItem>
            </Col>
          </Row>
        </FormItem>
        <FormItem label={t('course.details.edit.discount.date')} {...formItemSettings}>
          <FormItem
            name={['discount_dates']}
            rules={[
              {
                required: earlybirdDisabled && lastminuteDisabled,
                message: t('discount.dates.required'),
              },
            ]}
          >
            <RangePicker
              dropdownClassName="dropDown"
              placeholder={[t('course.details.content.start_date'), t('form.dates.end')]}
              disabled={discountDisabled}
            />
          </FormItem>
        </FormItem>
        <Divider />
        <FormItem label={t('course.details.edit.earlybird.date')} {...formItemSettings}>
          <FormItem
            name={['early_bird_dates']}
            rules={[
              {
                required: discountDisabled && lastminuteDisabled,
                message: t('discount.dates.required'),
              },
            ]}
          >
            <RangePicker
              dropdownClassName="dropDown"
              placeholder={[t('course.details.content.start_date'), t('form.dates.end')]}
              disabled={earlybirdDisabled}
            />
          </FormItem>
        </FormItem>
        <FormItem
          label={t('course.details.edit.earlybird.discount')}
          name={['early_bird_discount']}
          {...formItemSettings}
        >
          <InputNumber disabled={earlybirdDisabled} />
        </FormItem>
        <FormItem
          label={t('course.details.edit.earlybird.message')}
          name={['early_bird_message']}
          {...formItemSettings}
        >
          <TextArea disabled={earlybirdDisabled} />
        </FormItem>
        <Divider />
        <FormItem label={t('course.details.edit.lastminute.date')} {...formItemSettings}>
          <FormItem
            name={['last_minute_dates']}
            rules={[
              {
                required: discountDisabled && earlybirdDisabled,
                message: t('discount.dates.required'),
              },
            ]}
          >
            <RangePicker
              dropdownClassName="dropDown"
              placeholder={[t('course.details.content.start_date'), t('form.dates.end')]}
              disabled={lastminuteDisabled}
            />
          </FormItem>
        </FormItem>
        <FormItem
          label={t('course.details.edit.lastminute.discount')}
          name={['last_minute_discount']}
          {...formItemSettings}
        >
          <InputNumber disabled={lastminuteDisabled} />
        </FormItem>
        <FormItem
          label={t('course.details.edit.lastminute.message')}
          name={['last_minute_message']}
          {...formItemSettings}
        >
          <TextArea disabled={lastminuteDisabled} />
        </FormItem>
      </Form>
    </Modal>
  );
};
PriceModal.propTypes = {
  priceModal: PropTypes.bool,
  setPriceModal: PropTypes.func,
};

PriceModal.defaultProps = {
  priceModal: false,
  setPriceModal: () => {},
};

export default PriceModal;
